/**
 * Smart Editor Store Module
 *
 * Manages state and operations for TinyMCE editor instances with mapping capabilities
 *
 * Provides the following features:
 * - Font size management
 * - Document content handling
 * - Element selection and highlighting
 * - Mapping data synchronization
 * - Element scrolling and visibility
 * - Active element tracking
 *
 * @module SmartEditorStore
 */

import { makeObservable, observable, action, computed } from 'mobx';
import { IMappingSessionStore } from '../../../stores';
import { AIResponse, GeneralizationSuggestion, MappingTypeEnum } from '../../../types';
import { LIGHT_BLUE_COLOR } from '../../../constants';
import { SmartEditorType } from '../SmartEditor';
import { SmartEditingElement, SourceSmartEditorStore, TargetNodeMap } from '..';
import { generateRandomColor } from '../../../helpers/generalizationHelpers';

/**
 * Smart Editor Store Interface
 * Defines contract for editor state and operations management
 *
 * @interface ISmartEditorStore
 * @property {number} fontSize - Current font size percentage
 * @property {Document} editorDocument - Editor document instance
 * @property {AIResponse} aiSuggestions - Current AI mapping suggestions
 * @property {GeneralizationSuggestion[]} acceptedSuggestions - Accepted mapping suggestions
 * @property {MappingTypeEnum} mappingType - Current mapping type
 * @property {number} sourceFileId - Currently selected source file ID
 * @property {Function} setFontSize - Updates font size value
 * @property {Function} handleMappingDataLoaded - Handles mapping data initialization
 * @property {Function} handleElementSelection - Processes element selection events
 * @property {Function} setEditorDocument - Sets editor document instance
 * @property {Function} setActiveElement - Sets and highlights active element
 * @property {Function} deactivateElements - Removes active state from elements
 * @property {Function} handleTextSelection - Handles text selection events
 * @property {Function} getSelectedContent - Gets selected content from the editor
 * @property {Function} dispose - Cleans up resources
 */
export interface ISmartEditorStore {
  fontSize: number;
  editorDocument?: Document;
  isUserInteraction: boolean;
  editingElement?: SmartEditingElement;
  editingOverlay?: {
    nodeId: string;
    element: HTMLElement;
    rect: DOMRect;
    content: string;
  };

  readonly sourceNodeMap: string[];
  readonly targetNodeMap: TargetNodeMap;
  readonly aiSuggestions?: AIResponse;
  readonly acceptedSuggestions?: GeneralizationSuggestion[];
  readonly mappingType?: MappingTypeEnum;
  readonly sourceFileId?: number;

  setFontSize: (value: number) => void;
  handleMappingDataLoaded: () => void;
  handleElementSelection: (element: Element, isMultiSelect: boolean) => void;
  deactivateSelection(nodeId?: string): void;
  setEditorDocument: (doc: Document) => void;
  setActiveElement: (element: Element | null) => void;
  deactivateElements: (color?: string) => void;
  handleTextSelection: (range: Range | null, element: Element) => void;
  getSelectedContent: () => string | undefined;
  setIsUserInteraction: (isUserInteraction: boolean) => void;
  setEditingOverlay: (
    overlay: { nodeId: string; element: HTMLElement; rect: DOMRect; content: string } | undefined,
  ) => void;
  dispose: () => void;
}

export abstract class SmartEditorStore implements ISmartEditorStore {
  /** Current font size percentage */
  fontSize = 100;

  isUserInteraction = false;

  editingElement?: SmartEditingElement;

  editingOverlay?: {
    nodeId: string;
    element: HTMLElement;
    rect: DOMRect;
    content: string;
  };

  protected disposers: (() => void)[] = [];

  constructor(protected readonly mappingSessionStore: IMappingSessionStore) {
    makeObservable(this, {
      fontSize: observable,
      editingElement: observable,
      editingOverlay: observable,
      aiSuggestions: computed,
      acceptedSuggestions: computed,
      mappingType: computed,
      sourceFileId: computed,
      setFontSize: action,
      setEditingOverlay: action,
    });
  }

  /**
   * Handle mapping data loaded event
   * @abstract
   */
  abstract handleMappingDataLoaded(): void;

  /**
   * Handle element selection event
   * @abstract
   */
  abstract handleElementSelection(element: Element, isMultiSelect: boolean): void;

  abstract deactivateSelection(nodeId?: string): void;

  get editorDocument() {
    return this.mappingSessionStore.editorDocument.get(this.editorType);
  }

  get sourceNodeMap() {
    return this.mappingSessionStore.sourceNodeMap;
  }

  get targetNodeMap() {
    return this.mappingSessionStore.targetNodeMap;
  }

  /**
   * Get current AI suggestions from mapping session
   *
   * @readonly
   * @returns {AIResponse | undefined} Current AI suggestions
   */
  get aiSuggestions() {
    return this.mappingSessionStore.aiSuggestions;
  }

  /**
   * Get accepted suggestions from mapping session
   *
   * @readonly
   * @returns {GeneralizationSuggestion[] | undefined} Current accepted suggestions
   */
  get acceptedSuggestions() {
    return this.mappingSessionStore.acceptedSuggestions;
  }

  /**
   * Get current mapping type
   *
   * @readonly
   * @returns {MappingTypeEnum | undefined} Current mapping type
   */
  get mappingType() {
    return this.mappingSessionStore.mappingType;
  }

  /**
   * Get currently selected value
   *
   * @readonly
   * @returns {number} Selected value
   */
  get sourceFileId() {
    return this.mappingSessionStore.sourceFileId;
  }

  /**
   * Get editor type (source/target)
   * Determines the editor type based on store instance
   *
   * @property {SmartEditorType} editorType
   * @readonly
   * @returns {SmartEditorType} 'source' if SourceSmartEditorStore instance, 'target' otherwise
   *
   * @example
   * // In SourceSmartEditorStore instance
   * store.editorType // returns 'source'
   *
   * @example
   * // In TargetSmartEditorStore instance
   * store.editorType // returns 'target'
   */
  get editorType(): SmartEditorType {
    return this instanceof SourceSmartEditorStore ? 'source' : 'target';
  }

  /**
   * Set editor font size
   * Updates the font size percentage value
   *
   * @method setFontSize
   * @param {number} value - New font size percentage
   * @returns {void}
   *
   * @example
   * store.setFontSize(120);
   */
  setFontSize = (value: number) => {
    this.fontSize = value;
  };

  setIsUserInteraction = (isUserInteraction: boolean) => {
    this.isUserInteraction = isUserInteraction;
  };

  /**
   * Set editor document instance
   * Updates the reference to editor's document
   *
   * @method setEditorDocument
   * @param {Document} doc - Editor document instance
   * @returns {void}
   *
   * @example
   * store.setEditorDocument(editorDoc);
   */
  setEditorDocument = (doc: Document) => {
    this.mappingSessionStore.editorDocument.set(this.editorType, doc);
  };

  /**
   * Set active element in editor
   * Highlights selected element and its children
   *
   * @method setActiveElement
   * @param {Element | null} element - Element to activate
   * @returns {void}
   *
   * @example
   * store.setActiveElement(domElement);
   */
  setActiveElement = (element: Element | null) => {
    this.highlightElementWithChildren(element, LIGHT_BLUE_COLOR);
  };

  /**
   * Deactivate highlighted elements
   * Removes active state from all currently highlighted elements
   *
   * @method deactivateElements
   * @param {string} [color] - Optional color to apply after deactivation
   * @returns {void}
   *
   * @example
   * store.deactivateElements('transparent');
   */
  deactivateElements = (color?: string) => {
    this.editorDocument?.querySelectorAll('[data-highlight="active"]').forEach((el) => {
      this.highlightElementWithChildren(el, color);
    });
  };

  /**
   * Handle text selection changes in editor
   * Updates mapping session store with current selection state
   *
   * @method handleTextSelection
   * @param {Range | null} range - DOM Range object from selection, null when selection is cleared
   * @returns {void}
   * @throws {Error} When mapping session store is not available
   *
   * @example
   * // When text is selected
   * handleTextSelection(selectionRange);
   *
   * @example
   * // When selection is cleared
   * handleTextSelection(null);
   *
   * @remarks
   * - Stores both the selected text and its range
   * - Automatically determines editor type (source/target)
   * - Clears selection when range is null
   * - Synchronizes selection state with mapping session store
   */
  /**
   * Target editor overrides this to treat drag-selection across multiple table cells as
   * multi-cell node selection instead of partial text (base: no-op).
   */
  protected tryHandleMultiCellTableSelection(_range: Range): boolean {
    return false;
  }

  /**
   * Target editor may adjust cell highlighting when partial text is selected (base: no-op).
   */
  protected onPartialTextSelection(_element: Element): void {
    void _element;
  }

  handleTextSelection = (range: Range | null, element: Element) => {
    if (!range) {
      this.mappingSessionStore.clearTextSelection();
      return;
    }

    if (this.editorType === 'target' && this.tryHandleMultiCellTableSelection(range)) {
      return;
    }

    const selectedRange = range;
    const selectedText = range.toString();
    const editorType = this.editorType;
    this.mappingSessionStore.addTextSelection(editorType, selectedText);
    this.mappingSessionStore.addTextSelectionRange(editorType, selectedRange);

    // Partial text drag-selection: keep the browser range; do not re-run cell click handling.
    if (selectedText.trim().length > 0) {
      this.onPartialTextSelection(element);
      return;
    }

    if (this.editorType === 'source') {
      return;
    }
    const selectedNodeId = this.resolveTableCell(element).getAttribute('data-nodeid');
    if (!selectedNodeId) {
      return;
    }

    const currentNodes = this.mappingSessionStore.activeNodeIds.get(editorType) || [];

    // Do not clear the opposite editor's active nodes: partial text selection on the target
    // is used by Link / partial transfer and must keep the paired source cell selection.

    currentNodes.forEach((nodeId) => {
      if (selectedNodeId === nodeId) {
        this.handleElementSelection(element, false);
      } else {
        this.deactivateSelection(nodeId);
      }
    });
  };

  /**
   * Get HTML content of current text selection
   * Retrieves selected content from editor using stored range
   *
   * @method getSelectedContent
   * @returns {string | undefined} HTML content of selection, undefined if no selection exists
   * @throws {Error} When range cloning fails
   *
   * @example
   * // With active selection
   * const content = store.getSelectedContent();
   * // Returns: '<span>selected text</span>'
   *
   * @example
   * // Without selection
   * const content = store.getSelectedContent();
   * // Returns: undefined
   *
   * @remarks
   * - Uses stored range from mapping session store
   * - Preserves HTML structure of selected content
   * - Returns undefined when no selection exists
   * - Creates temporary DOM element to extract HTML
   */
  getSelectedContent = (): string | undefined => {
    const selectedTextRange = this.mappingSessionStore.selectedTextRange.get(this.editorType);
    if (!selectedTextRange) {
      return;
    }

    const div = document.createElement('div');
    div.appendChild(selectedTextRange.cloneContents());
    return div.innerHTML;
  };

  protected resolveTableCell = (element: Element): Element => {
    if (element.tagName === 'TABLE') {
      const cells = element.querySelectorAll('td, th');
      return cells[cells.length - 1];
    }
    return element.closest('td') || element.closest('th') || element.closest('p') || element;
  };

  /**
   * Scroll element into view
   * Ensures element is visible in editor iframe viewport
   *
   * @method scrollToElement
   * @param {Element | null} element - Element to scroll into view
   * @returns {void}
   * @throws {Error} When iframe or window access fails
   *
   * @example
   * // Internal usage
   * this.scrollToElement(domElement);
   */
  protected scrollToElement(element: Element | null) {
    if (!element || !this.editorDocument) return;

    // Get the editor's iframe
    const iframe = this.editorDocument.defaultView?.frameElement as HTMLIFrameElement;

    if (!iframe) {
      return;
    }

    const iframeWindow = iframe?.contentWindow;

    if (!iframeWindow) {
      return;
    }

    // Get element's position relative to viewport
    const rect = element.getBoundingClientRect();

    // Check if element is fully visible in iframe
    const isVisible =
      rect.top >= 0 &&
      rect.bottom <= iframeWindow.innerHeight &&
      rect.left >= 0 &&
      rect.right <= iframeWindow.innerWidth;

    if (!isVisible) {
      const htmlElement = element as HTMLElement;
      const iframeDoc = iframeWindow.document;

      // Get element position relative to iframe document
      const elementTop = htmlElement.offsetTop;
      const elementLeft = htmlElement.offsetLeft;

      // Calculate desired scroll position (center the element)
      const targetScrollTop = elementTop - iframe.clientHeight / 2;
      const targetScrollLeft = elementLeft - iframe.clientWidth / 2;

      // Ensure scroll position is within bounds
      const maxScrollTop = Math.max(
        0,
        iframeDoc.documentElement.scrollHeight - iframe.clientHeight,
      );
      const maxScrollLeft = Math.max(0, iframeDoc.documentElement.scrollWidth - iframe.clientWidth);

      const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      // Set scroll position directly on iframe document - NO scrollIntoView
      if (iframeDoc.documentElement) {
        iframeDoc.documentElement.scrollTop = finalScrollTop;
        iframeDoc.documentElement.scrollLeft = finalScrollLeft;
      }

      // Fallback for older browsers
      if (iframeDoc.body) {
        iframeDoc.body.scrollTop = finalScrollTop;
        iframeDoc.body.scrollLeft = finalScrollLeft;
      }
    }
  }

  /**
   * Highlight single element
   * Applies highlighting and active state to element
   *
   * @method highlightElement
   * @protected
   * @param {Element} element - Element to highlight
   * @param {string} [color='transparent'] - Background color for highlight
   * @param {boolean} [isActive=false] - Whether the element should be marked as active
   * @returns {void}
   */
  protected highlightElement(element: Element, color = 'transparent', isActive = false) {
    if (isActive) {
      this.setActiveAttributes(element);
    } else {
      this.setInactiveAttributes(element, color);
    }

    // Simply set the CSS properties we need without touching existing ones
    const htmlElement = element as HTMLElement;
    htmlElement.style.setProperty('background-color', color, 'important');
    htmlElement.style.setProperty('position', 'relative');

    // Apply to child elements
    element.querySelectorAll('*').forEach((child) => {
      const childElement = child as HTMLElement;
      childElement.style.setProperty('background-color', color, 'important');
      childElement.style.setProperty('position', 'relative');
    });
  }

  /**
   * Highlight element and its children
   * Recursively highlights all child elements with nodeId
   *
   * @method highlightElementWithChildren
   * @protected
   * @param {Element | null} element - Root element to highlight
   * @param {string} [color='transparent'] - Background color for highlight
   * @returns {void}
   */
  protected highlightElementWithChildren(element: Element | null, color = 'transparent') {
    if (!element) return;

    const isActive = color === LIGHT_BLUE_COLOR;
    const highlightColor = this.determineHighlightColor(color);

    // Highlight the main element
    this.highlightElement(element, highlightColor, isActive);
  }

  /**
   * Get element by node ID
   * Finds element in editor document by data-nodeid attribute
   *
   * @method getElementByNodeId
   * @protected
   * @param {string} nodeId - Node identifier to search for
   * @returns {Element | null | undefined} Matching element if found
   */
  protected getElementByNodeId(nodeId: string): Element | null | undefined {
    return this.editorDocument?.querySelector(`[data-nodeid="${nodeId}"]`);
  }

  /**
   * Get currently active element
   * Finds element with active highlight state
   *
   * @method getActiveElement
   * @protected
   * @returns {Element | null | undefined} Active element if found
   */
  protected getActiveElement(): Element | null | undefined {
    return this.editorDocument?.querySelector('[data-highlight="active"]');
  }

  /**
   * Determines the highlight color based on selection state and editor type
   * @private
   * @param {string} defaultColor - Default color to use if no special conditions apply
   * @returns {string} The color to use for highlighting
   */
  private determineHighlightColor(defaultColor: string): string {
    if (defaultColor !== LIGHT_BLUE_COLOR) {
      return defaultColor;
    }

    const currentSelection = this.mappingSessionStore.getActiveNodeIds(this.editorType);
    if (currentSelection.length <= 1) {
      return defaultColor;
    }

    const oppositeType: SmartEditorType = this.editorType === 'source' ? 'target' : 'source';
    const oppositeSelection = this.mappingSessionStore.getActiveNodeIds(oppositeType);

    if (oppositeSelection.length >= currentSelection.length) {
      const oppositeMatchNodeId = oppositeSelection[currentSelection.length - 1];
      const oppositeDoc = this.mappingSessionStore.editorDocument.get(oppositeType);
      const matchElement = oppositeDoc?.querySelector(`[data-nodeid="${oppositeMatchNodeId}"]`);

      if (matchElement) {
        return window.getComputedStyle(matchElement).backgroundColor;
      }
    }

    return generateRandomColor(currentSelection.length);
  }

  /**
   * Set element attributes for active state
   * Applies data attributes for active highlighting
   *
   * @method setActiveAttributes
   * @private
   * @param {Element} element - Element to activate
   * @returns {void}
   */
  private setActiveAttributes(element: Element) {
    element.setAttribute('data-highlight', 'active');
    element.setAttribute('data-highlighted', 'true');
    element.querySelectorAll('*').forEach((child) => {
      child.setAttribute('data-highlight', 'active');
      child.setAttribute('data-highlighted', 'true');
    });
  }

  /**
   * Set element attributes for inactive state
   * Removes or updates highlighting attributes
   *
   * @method setInactiveAttributes
   * @private
   * @param {Element} element - Element to deactivate
   * @param {string} color - Background color to apply
   * @returns {void}
   */
  private setInactiveAttributes(element: Element, color: string) {
    element.removeAttribute('data-highlight');
    element.querySelectorAll('*').forEach((child) => {
      child.removeAttribute('data-highlight');
    });

    if (color === 'transparent') {
      // Remove the highlighting properties we added
      const htmlElement = element as HTMLElement;
      htmlElement.style.removeProperty('background-color');
      htmlElement.style.removeProperty('position');

      element.removeAttribute('data-highlighted');

      element.querySelectorAll('*').forEach((child) => {
        const childElement = child as HTMLElement;
        childElement.style.removeProperty('background-color');
        childElement.style.removeProperty('position');
        child.removeAttribute('data-highlighted');
      });
      return;
    }

    // Apply highlighting color
    const htmlElement = element as HTMLElement;
    htmlElement.style.setProperty('background-color', color, 'important');
    htmlElement.style.setProperty('position', 'relative');

    element.setAttribute('data-highlighted', 'true');
    element.querySelectorAll('*').forEach((child) => {
      const childElement = child as HTMLElement;
      childElement.style.setProperty('background-color', color, 'important');
      childElement.style.setProperty('position', 'relative');
      child.setAttribute('data-highlighted', 'true');
    });
  }

  setEditingOverlay = (
    overlay: { nodeId: string; element: HTMLElement; rect: DOMRect; content: string } | undefined,
  ) => {
    this.editingOverlay = overlay;
  };

  dispose() {
    // Cleanup all reactions
    this.disposers.forEach((dispose) => dispose());
    this.disposers = [];
  }
}
